import { AggregatedMetricsTableData } from '../DisclosureRequirements/Metrics';

// Functions for cleaning up tables, follow the logic of the word report
const createRowIdentifier = (row: AggregatedMetricsTableData): string => {
  const reference = row.metric.reference;
  const tagName = row.subRows?.length && row.tagName ? row.tagName : null;
  const tagValues = row.tags?.length ? row.tags.map((t) => t.tagValue).join('-') : null;

  return [reference, tagName, tagValues].filter(Boolean).join('-');
};

export const processReportTableData = (
  data: AggregatedMetricsTableData,
  uniqueRowRefs: string[]
): AggregatedMetricsTableData => {
  const rowId = createRowIdentifier(data);
  const { metric, subRows } = data;
  const hasCalculation = metric.calculation !== '' && metric.calculation !== null;
  const isUmbrellaMetric = metric.calculation === 'NONE';

  // Check if this row already exists in the uniqueRowRefs
  const alreadyExists = uniqueRowRefs.includes(rowId);
  if (!alreadyExists) {
    uniqueRowRefs.push(rowId);
  }

  const withChildrenAndTags = metric.childrenMetrics?.length > 0 && !!subRows?.[0]?.tagName;

  // If the metric has calculation, we do not display nested rows
  if (hasCalculation && !withChildrenAndTags && !isUmbrellaMetric) {
    return { ...data, subRows: [] };
  } else if (alreadyExists) {
    return { ...data, subRows: [] };
  } else {
    // Process nested rows
    const processedSubRows = createNestedDataPointsRows(data, uniqueRowRefs);
    return { ...data, subRows: processedSubRows };
  }
};

const createNestedDataPointsRows = (
  metricResults: AggregatedMetricsTableData,
  uniqueRowRefs: string[]
): AggregatedMetricsTableData[] => {
  const subRows = metricResults.subRows;

  if (!subRows?.length) return [];

  const isUmbrellaMetric = metricResults.metric.calculation === 'NONE';

  const isTagWithChildMetrics =
    metricResults.metric.childrenMetrics?.length > 0 && metricResults.tagName;

  const withChildrenAndTags = isTagWithChildMetrics && metricResults.tagType;

  // Case for tag with child metric subRows => show only the tag
  if (isTagWithChildMetrics && !isUmbrellaMetric && !withChildrenAndTags) return [];

  return subRows.flatMap((row) => {
    const uniqueIdentifier = createRowIdentifier(row);
    const alreadyExists = uniqueRowRefs.includes(uniqueIdentifier);

    if (!alreadyExists) {
      uniqueRowRefs.push(uniqueIdentifier);
    }

    // Process the row itself
    const processedRow = { ...row };

    // Process its nested rows if it wasn't seen before
    if (!alreadyExists) {
      const nestedRows = createNestedDataPointsRows(row, uniqueRowRefs);
      processedRow.subRows = nestedRows;
    } else {
      processedRow.subRows = [];
    }

    return processedRow;
  });
};
